import { Box, Card, CardContent, Typography, Button, Stack, CircularProgress, IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useTranslation } from 'react-i18next';
import { forecastView, whatIf } from './forecastView';
import { canSetAside, countedByFigure, sumSetAside, matchLines } from './setAside';
import { formatCents } from './money';
import { formatEasternTime, formatEasternClock } from './dates';

const NO_SET_ASIDE = new Set();

function Headline({ view }) {
  const { t } = useTranslation();
  if (view.kind === 'deposit') return t('calendar.card.deposit', { amount: formatCents(view.cents) });
  if (view.kind === 'surplus') return t('calendar.card.surplus', { amount: formatCents(view.cents) });
  if (view.kind === 'none') return t('calendar.card.none', { reason: view.reason });
  if (view.kind === 'pending') return t('calendar.card.pending');
  return t('calendar.card.missing');
}

function itemLabel(item) {
  if (item.display_only) return item.label || item.kind;
  if (item.kind === 'check') return `${item.payee} #${item.check_number}`;
  return `${item.is_estimate ? '~' : ''}${item.payee}`;
}

function SetAsideIcon({ item, setAside, onToggle }) {
  const { t } = useTranslation();
  if (!onToggle || !canSetAside(item)) return null;
  return (
    <IconButton size='small' sx={{ ml: 0.5, p: 0.25 }} aria-label={setAside ? t('calendar.setAside.off') : t('calendar.setAside.on')} onClick={() => onToggle(item)}>
      {setAside ? <VisibilityIcon fontSize='inherit' /> : <VisibilityOffOutlinedIcon fontSize='inherit' />}
    </IconButton>
  );
}

function rowProps(aside) {
  return { 'data-set-aside': aside ? 'true' : undefined, sx: { opacity: aside ? 0.5 : 1 } };
}

function SetAsideLine({ testId, aside, t }) {
  return (
    <Typography data-testid={testId} variant='body2' sx={{ mt: 0.5 }}>
      {t('calendar.card.setAsideLine', { count: aside.count, amount: formatCents(aside.cents) })}
    </Typography>
  );
}

function KnownSoFar({ nextBusinessDay, items, lang, t, setAside, onToggle }) {
  const aside = sumSetAside(items, setAside);
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant='body2'>{t('calendar.card.knownSoFar', { day: formatEasternTime(nextBusinessDay, lang) })}</Typography>
      {items.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>{t('calendar.card.nothingScheduled')}</Typography>
      ) : items.map((item, i) => {
        const isAside = canSetAside(item) && setAside.has(item.id);
        return (
          <Typography key={i} data-testid='known-row' variant='body2' {...rowProps(isAside)}>
            <Box component='span' sx={{ color: 'error.main', fontWeight: 500 }}>- {formatCents(item.amount_cents)}</Box> {itemLabel(item)}
            <SetAsideIcon item={item} setAside={isAside} onToggle={onToggle} />
          </Typography>
        );
      })}
      {aside.count > 0 && <SetAsideLine testId='known-row' aside={aside} t={t} />}
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>{t('calendar.card.addedAt730')}</Typography>
    </Box>
  );
}

function figureText(result, t) {
  if (result.kind === 'deposit') return `${t('calendar.card.depositLine')} ${formatCents(result.cents)}`;
  return t('calendar.card.surplus', { amount: formatCents(result.cents) });
}

export default function AccountCards({
  accounts, forecasts, bankAccountId, onRefresh, busy = {}, perms = {},
  today = null, nextBusinessDay = null, knownItems = [], refreshing = {}, refreshErrors = {},
  outflows = [], setAside = NO_SET_ASIDE, onToggleSetAside,
}) {
  const { t, i18n } = useTranslation();
  const canManage = Boolean(perms.CAL_MANAGE);
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
      {accounts.filter(a => a.role === 'operating' && a.id === bankAccountId).map(a => {
        const view = forecastView(forecasts[a.id], { canManage, today });
        const showKnownSoFar = view.kind === 'pending' || view.hidden;
        const showFigure = view.kind === 'deposit' || view.kind === 'surplus';
        const counted = outflows.filter(o => countedByFigure(o, { bankAccountId: a.id, forDate: view.forDate }));
        const pairs = matchLines(view.lines, counted);
        const chosen = pairs.filter(p => p.item != null && setAside.has(p.item.id));
        const aside = { count: chosen.length, cents: chosen.reduce((sum, p) => sum + Number(p.line.cents), 0) };
        const alt = aside.count > 0 ? whatIf(view, aside.cents) : null;
        return (
          <Card key={a.id} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant='overline'>{a.nickname} · {a.location}</Typography>
              {view.shadow && <Typography variant='body2' color='warning.main'>{t('calendar.card.shadow')}</Typography>}
              <Typography variant='h6'><Headline view={view} /></Typography>
              {view.balanceCents != null && (
                <Typography variant='body2' color={view.stale ? 'warning.main' : 'text.secondary'}>
                  {t('calendar.card.balance', { amount: formatCents(view.balanceCents), date: formatEasternTime(view.balanceAsOf, i18n.language) })}{view.stale ? ` · ${t('calendar.card.stale')}` : ''}
                </Typography>
              )}
              {busy[a.id] && <Typography variant='body2'>{t('calendar.card.busy')}</Typography>}
              {!view.hidden && (
                <Stack direction='row' spacing={1} sx={{ mt: 1 }} alignItems='center'>
                  <Button size='small' disabled={Boolean(refreshing[a.id])} onClick={() => onRefresh(a.id)}
                    startIcon={refreshing[a.id] ? <CircularProgress size={16} /> : undefined}>
                    {t('calendar.card.refresh')}
                  </Button>
                  {refreshing[a.id] && <Typography variant='body2'>{t('calendar.card.refreshing')}</Typography>}
                </Stack>
              )}
              {refreshErrors[a.id] && <Typography variant='body2' color='error.main'>{refreshErrors[a.id]}</Typography>}
              {showFigure && view.fresh && (
                <Typography variant='body2' sx={{ mt: 1, fontStyle: 'italic' }}>
                  {t('calendar.card.preview', { time: formatEasternClock(view.previewAt, i18n.language) })}
                </Typography>
              )}
              {showFigure && (
                <Box sx={{ mt: 1 }}>
                  {view.startCents != null && (
                    <Typography data-testid='breakdown-row' variant='body2'>
                      {t('calendar.card.balanceNow')} <Box component='span' sx={{ color: view.startCents < 0 ? 'error.main' : 'success.main', fontWeight: 500 }}>{formatCents(view.startCents)}</Box>
                    </Typography>
                  )}
                  {pairs.map(({ line, item }, i) => {
                    const isAside = item != null && setAside.has(item.id);
                    return (
                      <Typography key={i} data-testid='breakdown-row' variant='body2' {...rowProps(isAside)}>
                        <Box component='span' sx={{ color: line.sign === '-' ? 'error.main' : 'success.main', fontWeight: 500 }}>{line.sign} {formatCents(line.cents)}</Box> {line.label}
                        {item && <SetAsideIcon item={item} setAside={isAside} onToggle={onToggleSetAside} />}
                      </Typography>
                    );
                  })}
                  <Typography data-testid='breakdown-row' variant='body2' sx={{ fontWeight: 600, mt: 0.5 }}>
                    {view.kind === 'deposit' ? `${t('calendar.card.depositLine')} ${formatCents(view.cents)}` : t('calendar.card.surplus', { amount: formatCents(view.cents) })}
                  </Typography>
                  {aside.count > 0 && <SetAsideLine testId='breakdown-row' aside={aside} t={t} />}
                  {alt && (
                    <Typography data-testid='breakdown-row' variant='body2' sx={{ fontWeight: 600 }}>
                      {t('calendar.card.ifTheyStayOut', { figure: figureText(alt, t) })}
                    </Typography>
                  )}
                </Box>
              )}
              {showKnownSoFar && (
                <KnownSoFar nextBusinessDay={nextBusinessDay} items={knownItems} lang={i18n.language} t={t} setAside={setAside} onToggle={onToggleSetAside} />
              )}
            </CardContent>
          </Card>
        );
      })}
      {accounts.filter(a => a.role === 'feeder').map(a => {
        const view = forecastView(forecasts[a.id], { canManage });
        return view.balanceCents == null ? null : (
          <Typography key={a.id} variant='body2' color='text.secondary'>{a.nickname}: {formatCents(view.balanceCents)}</Typography>
        );
      })}
    </Stack>
  );
}
